import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://wagerwatchdog.com';
const DEFAULT_TITLE =
  'WagerWatchdog — Independent Performance Ratings for Sports Betting Experts';
const DEFAULT_DESCRIPTION =
  'Independent performance ratings for sports betting experts. Every pick tracked, every outcome verified—see who actually performs over the long term.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/og-image.png`;

export type ArticleMeta = {
  ogType?: 'website' | 'article';
  ogImage?: string;
  ogImageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  tags?: string[];
  section?: string;
  twitterCard?: 'summary' | 'summary_large_image';
};

function setMetaContent(selector: string, attr: string, key: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setCanonicalUrl(url: string) {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
}

function setJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[]) {
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string) {
  document.getElementById(id)?.remove();
}

export default function PageMeta({
  title,
  fullTitle,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl: canonicalOverride,
  schema,
  schemaId = 'ww-page-jsonld',
  article,
}: {
  title?: string;
  fullTitle?: string;
  description?: string;
  canonicalUrl?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  schemaId?: string;
  article?: ArticleMeta;
}) {
  const { pathname } = useLocation();
  const pageTitle =
    fullTitle ?? (title ? `${title} | WagerWatchdog` : DEFAULT_TITLE);
  const canonicalPath = pathname.replace(/\/+$/, '') || '/';
  const canonicalUrl =
    canonicalOverride ??
    `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath}`;
  const ogType = article?.ogType ?? 'website';
  const ogImage = article?.ogImage ?? DEFAULT_OG_IMAGE;
  const twitterCard = article?.twitterCard ?? 'summary_large_image';

  useEffect(() => {
    document.title = pageTitle;
    setCanonicalUrl(canonicalUrl);
    setMetaContent('meta[name="title"]', 'name', 'title', pageTitle);
    setMetaContent('meta[name="description"]', 'name', 'description', description);
    setMetaContent('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaContent('meta[property="og:title"]', 'property', 'og:title', pageTitle);
    setMetaContent(
      'meta[property="og:description"]',
      'property',
      'og:description',
      description,
    );
    setMetaContent('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMetaContent('meta[property="og:image"]', 'property', 'og:image', ogImage);
    if (article?.ogImageAlt) {
      setMetaContent(
        'meta[property="og:image:alt"]',
        'property',
        'og:image:alt',
        article.ogImageAlt,
      );
    }
    if (article?.publishedTime) {
      setMetaContent(
        'meta[property="article:published_time"]',
        'property',
        'article:published_time',
        article.publishedTime,
      );
    }
    if (article?.modifiedTime) {
      setMetaContent(
        'meta[property="article:modified_time"]',
        'property',
        'article:modified_time',
        article.modifiedTime,
      );
    }
    if (article?.author) {
      setMetaContent(
        'meta[property="article:author"]',
        'property',
        'article:author',
        article.author,
      );
    }
    if (article?.section) {
      setMetaContent(
        'meta[property="article:section"]',
        'property',
        'article:section',
        article.section,
      );
    }
    for (const tag of article?.tags ?? []) {
      setMetaContent(
        `meta[property="article:tag"][content="${tag}"]`,
        'property',
        'article:tag',
        tag,
      );
    }
    setMetaContent('meta[name="twitter:url"]', 'name', 'twitter:url', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', 'name', 'twitter:title', pageTitle);
    setMetaContent(
      'meta[name="twitter:description"]',
      'name',
      'twitter:description',
      description,
    );
    setMetaContent('meta[name="twitter:card"]', 'name', 'twitter:card', twitterCard);
    setMetaContent('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    if (schema) {
      setJsonLd(schemaId, schema);
    }

    return () => {
      if (schema) {
        removeJsonLd(schemaId);
      }
    };
  }, [
    article?.author,
    article?.modifiedTime,
    article?.ogImageAlt,
    article?.publishedTime,
    article?.section,
    article?.tags,
    canonicalUrl,
    description,
    ogImage,
    ogType,
    pageTitle,
    schema,
    schemaId,
    twitterCard,
  ]);

  return null;
}

export { SITE_URL, DEFAULT_OG_IMAGE };
